import { CalculatorOutlined, CalendarOutlined, EyeOutlined, MinusCircleOutlined, MinusOutlined, OrderedListOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Divider, Drawer, Empty, Row, Select, Spin, Statistic, Switch, Tag, Typography } from "antd";
import dayjs from 'dayjs';
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List as RVList } from "react-virtualized";
import { SelectSearch } from "../../components/SelectSearch";
import { TreeSelectLocationUser } from "../../components/TreeSelectLocation";
import { UserPicker } from "../../components/UserPicker";
import Api from "../../services/api";
import { Container } from "../PowerBI/styles";
import ColoredBarChartWithDetails from "./charts/barchart";
import PieChartWithDetails from "./charts/piechart";
import DonutChartWithDetails from "./charts/pizzachart";
import LocationTree from "./locationTreeCustom";

const { Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

// Configura os ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// Função para renderizar mapa a partir de valor de geolocalização
// Formato esperado: "lat,lng;lat,lng;..."
const renderGeolocationMap = (value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
        return <Empty description="Nenhuma geolocalização válida" />;
    }

    let markers = [];

    // Função auxiliar para tentar converter uma string de coordenadas
    const parseCoordinate = (coordinateString) => {
        try {
            // Se não contém vírgula mas contém ponto-e-vírgula, interpretamos como "lat;lng"
            if (coordinateString.indexOf(",") === -1 && coordinateString.indexOf(";") !== -1) {
                const coords = coordinateString.split(";").map((p) => p.trim());
                if (coords.length === 2) {
                    const lat = parseFloat(coords[0]);
                    const lng = parseFloat(coords[1]);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        return { lat, lng };
                    }
                }
            } else {
                // Caso contrário, assume "lat,lng"
                const coords = coordinateString.split(",").map((p) => p.trim());
                if (coords.length === 2) {
                    const lat = parseFloat(coords[0]);
                    const lng = parseFloat(coords[1]);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        return { lat, lng };
                    }
                }
            }
        } catch (error) {
            console.error("Erro ao fazer parse da coordenada:", coordinateString, error);
        }
        return null;
    };

    // Se for um array, itera sobre cada item
    if (Array.isArray(value)) {
        value.forEach((item) => {
            if (!item) return; // ignora valores nulos ou falsos
            const coordinate = parseCoordinate(item);
            if (coordinate) {
                markers.push(coordinate);
            }
        });
    } else if (typeof value === "string") {
        try {
            // Se a string inteira é um único par "lat;lng"
            if (value.indexOf(",") === -1 && value.indexOf(";") !== -1) {
                const coordinate = parseCoordinate(value);
                if (coordinate) {
                    markers.push(coordinate);
                }
            } else {
                // Separa por ponto-e-vírgula para obter cada par "lat,lng"
                const parts = value.split(";").map((p) => p.trim()).filter((p) => p);
                parts.forEach((part) => {
                    const coordinate = parseCoordinate(part);
                    if (coordinate) {
                        markers.push(coordinate);
                    }
                });
            }
        } catch (error) {
            console.error("Erro ao processar a string de geolocalização:", value, error);
        }
    }

    if (markers.length === 0) {
        return <Empty description="Nenhuma geolocalização válida" />;
    }

    // Define o centro do mapa com base no primeiro marcador
    const center = [markers[0].lat, markers[0].lng];

    return (
        <MapContainer center={center} zoom={markers.length > 1 ? 5 : 10} style={{ height: 250, width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MarkerClusterGroup>
                {markers.map((marker, idx) => (
                    <Marker key={idx} position={[marker.lat, marker.lng]}>
                        <Popup>
                            <div>
                                <strong>Lat:</strong> {marker.lat}
                                <br />
                                <strong>Lng:</strong> {marker.lng}
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MarkerClusterGroup>
            <FullscreenControl />
        </MapContainer>
    );
};

const FullscreenControl = () => {
    const map = useMap();

    useEffect(() => {
        const handleFullscreen = () => {
            if (!document.fullscreenElement) {
                map.getContainer().requestFullscreen();
            } else {
                document.exitFullscreen();
            }
        };

        const fullscreenButton = L.control({ position: "topright" });

        fullscreenButton.onAdd = () => {
            const btn = L.DomUtil.create("button", "leaflet-bar leaflet-control leaflet-control-custom");
            btn.innerHTML = "⛶";
            btn.style.width = "30px";
            btn.style.height = "30px";
            btn.style.cursor = "pointer";
            btn.title = "Fullscreen";
            btn.onclick = handleFullscreen;
            return btn;
        };

        fullscreenButton.addTo(map);

        return () => {
            fullscreenButton.remove();
        };
    }, [map]);

    return null;
};

const NewPowerBI = ({ formId = null, userId = null }) => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [cols, setCols] = useState(2);
    const [forms, setForms] = useState([]);
    const [formsFilhos, setFormsFilhos] = useState([]);
    const [form, setForm] = useState();
    const [formFilho, setFormFilho] = useState();
    const [formsOptions, setFormsOptions] = useState([]);
    const [formsFilhosOptions, setFormsFilhosOptions] = useState([]);
    const [clear, setClear] = useState(false);
    const [tasksByLocation, setTasksByLocation] = useState([]);
    const [lastExecution, setLastExecution] = useState(null);

    const [taskIdsList, setTaskIdsList] = useState([]);

    // Mover antes dos useEffects
    const [filterAnswersRequest, setFilterAnswersRequest] = useState({
        userId: undefined,
        locationId: undefined,
        dateRange: [dayjs().startOf("month"), dayjs().endOf("month")],
        startDate: dayjs().startOf("month"),
        endDate: dayjs().endOf("month"),
        locationIncludeChildren: true
    });

    const loadedFormIdRef = useRef(null);

    useEffect(() => {
        Api.get(`PowerBI/GetForms`)
            .then(res => {
                const opts = res.data.response.map(i => ({ label: i.name, value: i.id }));
                setForms(res.data.response);
                setFormsOptions(opts);
                setLoading(false);
            })
            .catch(() => setLoading(false));

        Api.post(`PowerBI/LastExecution`)
            .then(res => {
                const dt = dayjs.utc(res.data.response).local().format("DD/MM/YYYY HH:mm");
                setLastExecution(dt);
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (formId && forms.length > 0 && !form && loadedFormIdRef.current != formId) {
            const selected = forms.find(f => f.id == formId);
            if (selected) {
                setForm(formId);
                setFormsFilhos(selected.childs || []);
                setFormsFilhosOptions(
                    (selected.childs || []).map(c => ({ label: `(v${c.version}) ${c.name}`, value: c.id }))
                );

                setFilterAnswersRequest(prev => ({
                    ...prev,
                    userId: userId ? [userId] : null,
                    dateRange: undefined,
                    startDate: undefined,
                    endDate: undefined,
                }));

                loadedFormIdRef.current = formId;
            }
        }
    }, [formId, forms, form, userId]);

    useEffect(() => {
        if (form && formId && loadedFormIdRef.current === formId) {
            fetchAnswers();
        }
    }, [form]);

    const renderAnswersUserDropdown = () => {
        return (
            <UserPicker
                placeholder={t("AssignedTo")}
                onChange={handleAnswersSearchUser}
                value={filterAnswersRequest.userId}
                disabled={!!!form || loading}
                count={0}
                style={{ width: "100%" }}
            />
        );
    };

    const renderAnswersLocationDropdown = () => {
        return (
            <TreeSelectLocationUser
                placeholder={t("Location")}
                includeDirectParents={false}
                disabled={!!!form || loading}
                onChange={(p) => handleAnswersSearchLocation(p, "combo")}
                value={filterAnswersRequest.locationId}
                style={{ width: "100%" }}
            />
        );
    };

    const renderAnswersLocationBelowDropdown = () => {
        if (filterAnswersRequest?.locationId != undefined && filterAnswersRequest?.locationId != "")
            return (
                <><Switch disabled={!!!form || loading} style={{ marginTop: 10 }} value={filterAnswersRequest?.locationIncludeChildren} onChange={(p) => handleAnswersSearchLocation(p, "switchbelow")} /> {t('History.LocationBelow')}</>
            );
        else return <></>
    };

    const onChange = async (value) => {
        const fx = forms.find((i) => i.id === value);
        if (fx) {
            setForm(value);
            setFormFilho(undefined);
            const opts = [];
            fx.childs.forEach((i) =>
                opts.push({
                    label: "(v" + i.version + ") " + i.name,
                    value: i.id,
                })
            );
            setFormsFilhos(fx.childs);
            setFormsFilhosOptions(opts);
        }
    };

    const onChangeFilho = async (value) => {
        if (!value) return;
        setFormFilho(formsFilhos.find((i) => i.id === value));
    };

    const handleAnswersSearchUser = (value) => {
        setFilterAnswersRequest((prev) => ({ ...prev, userId: value }));
    };

    const [shouldFetchAnswers, setShouldFetchAnswers] = useState(false);

    const handleAnswersSearchLocation = (value, field = "combo", triggerFetch = false) => {
        setFilterAnswersRequest((prev) => ({
            ...prev,
            locationId: field === "combo" ? value : prev.locationId,
            locationIncludeChildren: field === "switchbelow" ? value : prev.locationIncludeChildren,
        }));

        if (triggerFetch) {
            setShouldFetchAnswers(true);
        }
    };

    useEffect(() => {
        if (shouldFetchAnswers) {
            fetchAnswers();
            setShouldFetchAnswers(false);
        }
    }, [filterAnswersRequest.locationId, shouldFetchAnswers]);

    const handleDateRangeChange = (dates) => {
        if (dates) {
            const [start, end] = dates.map((date) => date.toISOString());
            setFilterAnswersRequest((prev) => ({
                ...prev,
                dateRange: dates,
                startDate: start,
                endDate: end,
            }));
        } else {
            setFilterAnswersRequest((prev) => ({
                ...prev,
                startDate: undefined,
                endDate: undefined,
            }));
        }
    };

    const [questions, setQuestions] = useState(null);
    const [formDataMax, setFormDataMax] = useState(null);
    const [formAnswers, setFormAnswers] = useState(null);
    const [taskStatusAnswers, setTaskStatusAnswers] = useState(0);
    const [lastLoadedForm, setLastLoadedForm] = useState(null);
    const [lastLoadedFormFilho, setLastLoadedFormFilho] = useState(null);

    const renderChart = (x2, data, categories) => {
        const activeFilters = selectedFilters[x2.previousVersionId ?? x2.id] || [];
        switch (x2.chartType) {
            case "barChart":
                return (
                    <ColoredBarChartWithDetails
                        data={data}
                        categories={categories}
                        disabled={loading}
                        activeFilters={activeFilters}
                        onClick={(valor) =>
                            handleFilterSelect(x2.previousVersionId ?? x2.id, valor)
                        }
                    />
                );
            case "pieChart":
                return (
                    <PieChartWithDetails
                        data={data}
                        categories={categories}
                        disabled={loading}
                        activeFilters={activeFilters}
                        onClick={(valor) =>
                            handleFilterSelect(x2.previousVersionId ?? x2.id, valor)
                        }
                    />
                );
            case "donutChart":
                return (
                    <DonutChartWithDetails
                        data={data}
                        categories={categories}
                        disabled={loading}
                        activeFilters={activeFilters}
                        onClick={(valor) =>
                            handleFilterSelect(x2.previousVersionId ?? x2.id, valor)
                        }
                    />
                );
            default:
                return <p>{t("Dashboard.UnsupportedChartType")}</p>;
        }
    };

    const renderAgr = (x2, data, categories) => {
        const numericCategories = categories.map((cat) => parseFloat(cat) || 0);
        const totalValue = numericCategories.reduce(
            (sum, value, index) => sum + value * (data[index] || 0),
            0
        );
        const totalReports = data.reduce((sum, count) => sum + count, 0);
        const aggregatedValue =
            x2.agregationType === "Sum"
                ? totalValue
                : x2.agregationType === "Avg"
                    ? totalReports > 0
                        ? totalValue / totalReports
                        : 0
                    : null;

        if (aggregatedValue !== null) {
            if (x2.agregationType === "Sum")
                return (
                    <Statistic
                        title={t("Dashboard.Sum")}
                        value={aggregatedValue.toFixed(2)}
                        precision={2}
                        valueStyle={{ color: "#1B3A7C", fontSize: 60 }}
                        prefix={<CalculatorOutlined />}
                    />
                );
            else if (x2.agregationType === "Avg")
                return (
                    <Statistic
                        title={t("Dashboard.Avg")}
                        value={aggregatedValue.toFixed(2)}
                        precision={2}
                        valueStyle={{ color: "#1B3A7C", fontSize: 60 }}
                        prefix={<CalculatorOutlined />}
                    />
                );
            else return null;
        }
        return null;
    };

    const [refresh, setRefresh] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);

    const handleFilterSelect = (questionId, valor) => {
        setSelectedFilters((prevFilters) => {
            const currentValues = prevFilters[questionId] || [];
            const newValues = currentValues.includes(valor)
                ? currentValues.filter((item) => item !== valor)
                : [...currentValues, valor];
            const updatedFilters = { ...prevFilters };
            if (newValues.length > 0) {
                updatedFilters[questionId] = newValues;
            } else {
                delete updatedFilters[questionId];
            }
            setRefresh(true);
            return updatedFilters;
        });
    };

    useEffect(() => {
        if (refresh) {
            fetchAnswers();
            setRefresh(false);
        }
    }, [refresh]);

    const fetchAnswers = async () => {
        if (!form) return;
        setLoading(true);

        if (form != lastLoadedForm || formFilho != lastLoadedFormFilho) {
            setFormDataMax(null);
            setSelectedFilters([]);
            await Api.post(`PowerBI/getFormData/${form}${formFilho ? "/" + formFilho?.id : ""}`)
                .then((res) => {
                    const itemWithMaxVersion = res.data.response?.reduce(
                        (maxItem, currentItem) =>
                            currentItem.version > maxItem.version ? currentItem : maxItem,
                        res.data.response[0]
                    );
                    const allQuestions = res.data.response.flatMap((form) =>
                        form.sections?.flatMap((section) =>
                            section.fields?.map((field) => ({
                                id: field.id,
                                label: field.label,
                                type: field.questionTypeCode,
                                chartType: field.chartType,
                                visualTitle: field.visualTitle,
                                visualSubTitle: field.visualSubTitle,
                                agregationType: field.agregationType || null,
                                version: field.version,
                                baseId: field.previousVersionId,
                                questionTypeCode: field.questionTypeCode,
                                isAtr: field.isAtr
                            })) || []
                        ) || []
                    );
                    setQuestions(allQuestions);
                    setFormDataMax(itemWithMaxVersion);
                    setLastLoadedForm(form);
                    setLastLoadedFormFilho(formFilho?.id);
                });
        }

        const params = {
            ...filterAnswersRequest,
            formId: form,
            versionId: formFilho?.id,
            filters: Object.entries(selectedFilters).map(([questionId, answers]) => {
                const findQuestion = formAnswers?.find(
                    (answer) =>
                        answer.questionId?.toLowerCase() === questionId.toLowerCase() ||
                        answer.originBaseId?.toLowerCase() === questionId.toLowerCase()
                );
                return {
                    QuestionId: findQuestion?.questionId ?? questionId,
                    OriginBaseId: findQuestion?.originBaseId || undefined,
                    Answers: answers.filter((x) => x),
                };
            }),
        };
        var answers = await Api.post(`PowerBI/getFormAnswers`, params);
        setFormAnswers(answers.data.response.answers);
        setTaskStatusAnswers(answers.data.response.filteredTasks);
        setTasksByLocation(answers.data.response.filteredTasksByLocation || []);
        setTaskIdsList(answers.data.response.tasks || []);
        setLoading(false);
    };

    useEffect(() => {
        if (!loading && clear) {
            fetchAnswers();
            setClear(false);
        }
    }, [clear]);

    const clearFilters = () => {
        setSelectedFilters([]);
        setClear(true);
    };

    const onClearFilter = async (target) => {
        if (target === "pai") {
            setForm(undefined);
        } else if (target === "filho") {
            setFormFilho(undefined);
        }
    };

    function crossFilter() {
        fetchAnswers();
    }

    const formatDate = (data) => (data ? moment(data).format('YYYY-MMM-DD HH:mm') : "—");

    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 125
        })
    );

    return (
        <Container>
            <Row align="middle" justify="center">
                <Col span={24}>
                    <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                        Last Update: {lastExecution}
                    </Typography.Text>
                    <Row gutter={[8, 8]}>
                        {!formId && <Col span={4}>
                            <Typography.Text>{t("Dashboard.FilterByForm")}</Typography.Text>
                            <br />
                            <SelectSearch
                                placeholder={t("BtnSearch")}
                                onChange={onChange}
                                optionsData={formsOptions}
                                onClear={() => onClearFilter("pai")}
                                style={{ width: "100%" }}
                                disabled={loading || formId}
                            />
                        </Col>}
                        <Col span={4}>
                            <Typography.Text>{t("Dashboard.FilterByVersionOptional")}</Typography.Text>
                            <br />
                            <SelectSearch
                                placeholder={t("Version")}
                                onChange={onChangeFilho}
                                onClear={() => onClearFilter("filho")}
                                optionsData={formsFilhosOptions}
                                style={{ width: "100%" }}
                                disabled={loading}
                            />
                        </Col>
                        <Col span={4}>
                            <Typography.Text>{t("Dashboard.FilterByDateRange")}</Typography.Text>
                            <br />
                            <DatePicker.RangePicker
                                value={filterAnswersRequest?.dateRange}
                                onChange={handleDateRangeChange}
                                style={{ width: "100%" }}
                                disabled={loading}
                            />
                        </Col>
                        {!userId && <Col span={4}>
                            <Typography.Text>{t("Dashboard.ResponsibleUserFilter")}</Typography.Text>
                            <br />
                            {renderAnswersUserDropdown()}
                        </Col>}
                        <Col span={4}>
                            <Typography.Text>{t("Dashboard.FilterByLocation")}</Typography.Text>
                            <br />
                            {renderAnswersLocationDropdown()}
                            <br />
                            {renderAnswersLocationBelowDropdown()}
                        </Col>
                        <Col span={4}>
                            <Typography.Text>{t("Dashboard.HowManyColumns")}</Typography.Text>
                            <br />
                            <Select
                                defaultValue={2}
                                onChange={(e) => setCols(e)}
                                style={{ width: "100%", marginBottom: 10 }}
                            >
                                <Select.Option value={1}>{t("Dashboard.OneColumn")}</Select.Option>
                                <Select.Option value={2}>{t("Dashboard.TwoColumns")}</Select.Option>
                                <Select.Option value={3}>{t("Dashboard.ThreeColumns")}</Select.Option>
                                <Select.Option value={4}>{t("Dashboard.FourColumns")}</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                    <Row gutter={[8, 8]}>
                        <Col>
                            <Typography.Text style={{ fontSize: 20, marginRight: 25 }}>
                                {t("Dashboard.Tasks")}: {taskStatusAnswers}
                            </Typography.Text>
                        </Col>
                        <Col>
                            <Button
                                type="default"
                                icon={<MinusCircleOutlined />}
                                disabled={Object.keys(selectedFilters).length === 0 || loading}
                                onClick={clearFilters}
                            >
                                {t("Dashboard.ClearCrossFilter")}
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                style={{ marginLeft: 5 }}
                                disabled={loading}
                                onClick={() => crossFilter()}
                            >
                                {t("Dashboard.Filter")}
                            </Button>
                        </Col>
                    </Row>

                    <Row gutter={[8, 8]}>
                        <Col span={24}>
                            <Row gutter={[8]}>
                                <Col span={24} style={{ marginTop: 10 }}>
                                    <Card
                                        title={
                                            <>
                                                {t("Dashboard.TasksByLocation")}{" "}
                                                {filterAnswersRequest.locationId ? (
                                                    <Button
                                                        type="primary"
                                                        icon={<MinusOutlined />}
                                                        style={{ marginLeft: 5 }}
                                                        disabled={loading}
                                                        onClick={() =>
                                                            handleAnswersSearchLocation(null, "combo", true)
                                                        }
                                                    >
                                                        {t("Dashboard.ClearLocation")}
                                                    </Button>
                                                ) : (
                                                    ""
                                                )}
                                            </>
                                        }
                                        bodyStyle={{ padding: 12 }}
                                    >
                                        {loading ? (
                                            <Spin />
                                        ) : (
                                            <LocationTree
                                                tasksByLocation={tasksByLocation}
                                                handleAnswersSearchLocation={handleAnswersSearchLocation}
                                            />
                                        )}
                                    </Card>
                                </Col>
                            </Row>

                            {formDataMax?.sections
                                .filter(section => section.fields.length > 0)
                                .map(section => (
                                    <Row gutter={[8, 8]} key={section.id} style={{ display: 'flex', flexWrap: 'wrap' }}>
                                        <Col span={24} style={{ marginTop: 20 }}>
                                            <h2 style={{ marginBottom: 0 }}>
                                                {t("Comp.Section")}: {section.section}
                                            </h2>
                                            {loading && (
                                                <div style={{ textAlign: 'center' }}>
                                                    <Spin />
                                                </div>
                                            )}
                                        </Col>

                                        {section.fields.map(x2 => {
                                            // 1) busca no questions para pegar o baseId correto
                                            const x2question = questions.find(q => q.id === x2.id);

                                            // 2) monta o array completo de IDs (versão atual + todas as versões ligadas), em lowercase
                                            let ids = [];
                                            if (x2question?.baseId) {
                                                const baseQuestions = questions.filter(
                                                    q => q.baseId === x2question.baseId || q.id === x2question.baseId
                                                );
                                                baseQuestions.forEach(q => ids.push(q.id.toLowerCase()));
                                            } else {
                                                ids.push(x2.id.toLowerCase());
                                            }

                                            // 3) filtra tudo de uma vez (questionId OU originBaseId), usando lowercase
                                            let answers = (formAnswers || []).filter(ans =>
                                                (ans.questionId && ids.includes(ans.questionId.toLowerCase())) ||
                                                (ans.originBaseId && ids.includes(ans.originBaseId.toLowerCase()))
                                            );

                                            // 3.1) se houver mix de com e sem originBaseId, mantém só os sem
                                            const hasWithBase = answers.some(a => a.originBaseId);
                                            const hasWithoutBase = answers.some(a => !a.originBaseId);
                                            if (hasWithBase && hasWithoutBase) {
                                                answers = answers.filter(a => !a.originBaseId);
                                            }

                                            // 4) pula se não tiver respostas e não houver nem chartType nem agregationType
                                            if (answers.length === 0 && !x2.chartType && !x2.agregationType) {
                                                return null;
                                            }

                                            // 5) monta counts, categories e data
                                            const answerCounts = answers.reduce((acc, curr) => {
                                                const key = curr.answer || 'N/A';
                                                acc[key] = (acc[key] || 0) + curr.answerCount;
                                                return acc;
                                            }, {});
                                            const categories = Object.keys(answerCounts);
                                            const data = Object.values(answerCounts);

                                            // 6) geolocation?
                                            if (x2.questionTypeCode === 'geolocation') {
                                                const geoValues = answers.map(a => a.answer);
                                                return (
                                                    <Col span={24 / cols} key={x2.id + '-map'}>
                                                        <Card className="mt-10" bodyStyle={{ padding: 12 }} title={x2.visualTitle ?? x2.label}>
                                                            {loading ? <Spin /> : renderGeolocationMap(geoValues)}
                                                        </Card>
                                                    </Col>
                                                );
                                            }

                                            if (!data || data.length == 0 || loading) return <></>;
                                            // 7) renderiza agregação Sum/Avg e gráficos
                                            return (
                                                <React.Fragment key={x2.id}>
                                                    {x2.agregationType && (
                                                        <Col span={24 / cols} style={{ display: 'flex', flexDirection: 'column', height: "100%" }}>
                                                            <Card className="mt-10" style={{ flex: 1 }} bodyStyle={{ padding: 12 }} title={x2.visualTitle ?? x2.label}>
                                                                {loading ? <Spin /> : renderAgr(x2, data, categories)}
                                                                <small>{x2.visualSubTitle}</small>
                                                            </Card>
                                                        </Col>
                                                    )}
                                                    {x2.chartType && (
                                                        <Col span={24 / cols} style={{ display: 'flex', flexDirection: 'column', height: "100%" }}>
                                                            <Card className="mt-10" bodyStyle={{ padding: 12 }} title={x2.visualTitle ?? x2.label}>
                                                                {loading ? <Spin /> : renderChart(x2, data, categories)}
                                                                <small>{x2.visualSubTitle}</small>
                                                            </Card>
                                                        </Col>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </Row>
                                ))}
                        </Col>
                    </Row>

                    {taskIdsList && taskIdsList.length > 0 ?
                        <Card className="mt-10" bodyStyle={{ padding: "10px 0px 0px 0px" }} title={t("Dashboard.Tasks")}>
                            <div style={{ height: 500 }}>
                                {loading ? <Spin /> :
                                    <>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '8px 12px',
                                                borderBottom: '1px solid #e8e8e8',
                                                background: '#fff',
                                                fontSize: 14,
                                                fontWeight: '600',
                                                textTransform: 'uppercase',
                                                userSelect: 'none'
                                            }}
                                        >
                                            <div style={{ width: 60, flexShrink: 0 }}>LINK</div>
                                            <div style={{ width: 120, flexShrink: 0 }}>{t('Code')}</div>
                                            <div style={{ width: 200, flexShrink: 0 }}>{t('Form')}</div>
                                            <div style={{ width: 250, flexShrink: 0 }}>{t('Description')}</div>
                                            <div style={{ width: 80, flexShrink: 0 }}>{t('Version')}</div>
                                            <div style={{ width: 200, flexShrink: 0 }}>{t('Location')}</div>
                                            <div style={{ width: 150, flexShrink: 0 }}>{t('CreatedAt')}</div>
                                            <div style={{ width: 150, flexShrink: 0 }}>{t('AssignedTo')}</div>
                                            <div style={{ width: 150, flexShrink: 0 }}>{t('DueDate')}</div>
                                        </div>

                                        <AutoSizer>
                                            {({ width, height }) => (
                                                <RVList
                                                    width={width}
                                                    height={height - 48}  // subtrai altura aproximada do header
                                                    rowCount={taskIdsList.length}
                                                    deferredMeasurementCache={cache.current}
                                                    rowHeight={cache.current.rowHeight}
                                                    overscanRowCount={3}
                                                    rowClassName="virtualized-table-row"
                                                    rowRenderer={({ key, index, style, parent }) => {
                                                        const r = taskIdsList[index];
                                                        return (
                                                            <CellMeasurer
                                                                cache={cache.current}
                                                                columnIndex={0}
                                                                key={key}
                                                                parent={parent}
                                                                rowIndex={index}
                                                            >
                                                                {({ registerChild }) => (
                                                                    <div
                                                                        ref={registerChild}
                                                                        style={{
                                                                            ...style,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            padding: '8px 12px',
                                                                            borderBottom: '1px solid #f0f0f0',
                                                                            fontSize: 14,
                                                                            background: index % 2 ? '#fafafa' : '#fff'
                                                                        }}
                                                                    >
                                                                        <div style={{ width: 60, flexShrink: 0 }}>
                                                                            <a
                                                                                href={`/formbuilder/view-answer/${r.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{ color: '#1890ff' }}
                                                                            >
                                                                                🔗
                                                                            </a>
                                                                        </div>
                                                                        <div style={{ width: 120, flexShrink: 0 }}>
                                                                            {r.friendlyId ?? r.id}
                                                                        </div>
                                                                        <div style={{ width: 200, flexShrink: 0 }}>
                                                                            {r.formName}
                                                                        </div>
                                                                        <div style={{
                                                                            width: 250,
                                                                            flexShrink: 0,
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}>
                                                                            {r.description || r.name}
                                                                        </div>
                                                                        <div style={{ width: 80, flexShrink: 0 }}>
                                                                            {r.version + 1}
                                                                        </div>
                                                                        <div style={{ width: 200, flexShrink: 0 }}>
                                                                            {r.locationName}
                                                                        </div>
                                                                        <div style={{ width: 150, flexShrink: 0 }}>
                                                                            {formatDate(r.insertDate)}
                                                                        </div>
                                                                        <div style={{ width: 150, flexShrink: 0 }}>
                                                                            {r.userName}
                                                                        </div>
                                                                        <div style={{ width: 150, flexShrink: 0 }}>
                                                                            {formatDate(r.scheduleDate)}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </CellMeasurer>
                                                        );
                                                    }}
                                                />
                                            )}
                                        </AutoSizer>
                                    </>
                                }
                            </div>
                        </Card>
                        : ""}
                </Col>
            </Row >
        </Container >
    );
};

export default NewPowerBI;
